/**
 * One orders sync loop per venue, shared by every component that reads the
 * board (ChefOverview, TableBoard): the first to mount starts it, the last to
 * unmount stops it. `ordersSyncFor` lets the query run the first full read
 * before the effect that starts the loop.
 */
import { ordersApi } from "../../api/client";
import { isKitchenWsConnected } from "../ws-status";
import { registerSyncHandlers } from "./feed-trigger";
import { kitchenFreshness } from "./kitchen-freshness";
import { kitchenMetrics } from "./kitchen-metrics";
import { createOrdersSync, type CacheLike, type OrdersSync } from "./orders-sync";

interface Entry {
  sync: OrdersSync;
  refs: number;
  unregister?: () => void;
  stopMetrics?: () => void;
}

export interface SyncQueryClient extends CacheLike {
  invalidateQueries(filters: { queryKey: readonly unknown[] }): unknown;
}

const entries = new Map<string, Entry>();

function entryFor(clientId: string, queryClient: SyncQueryClient): Entry {
  let entry = entries.get(clientId);
  if (!entry) {
    entry = {
      refs: 0,
      sync: createOrdersSync({
        clientId,
        queryClient,
        api: ordersApi,
        freshness: kitchenFreshness,
        metrics: kitchenMetrics,
        isSocketUp: isKitchenWsConnected,
        // The station view keeps its own query (a different shape): tell it to
        // re-read when the board changed.
        onChanges: () => queryClient.invalidateQueries({ queryKey: ["kitchen-station-orders"] }),
      }),
    };
    entries.set(clientId, entry);
  }
  return entry;
}

export function ordersSyncFor(clientId: string, queryClient: SyncQueryClient): OrdersSync {
  return entryFor(clientId, queryClient).sync;
}

/** Starts (or joins) the loop. Returns the release. */
export function acquireOrdersSync(clientId: string, queryClient: SyncQueryClient): () => void {
  const entry = entryFor(clientId, queryClient);
  entry.refs += 1;
  if (entry.refs === 1) {
    entry.unregister = registerSyncHandlers({
      pollNow: entry.sync.triggerPoll,
      reloadAll: entry.sync.requestFullReload,
    });
    entry.stopMetrics = kitchenMetrics.start();
    entry.sync.start();
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    entry.refs -= 1;
    if (entry.refs === 0) {
      entry.unregister?.();
      entry.stopMetrics?.();
      entry.sync.stop();
      entries.delete(clientId);
    }
  };
}
